/*
 * Relic Effects Helpers
 *
 * This module centralizes all logic related to applying the effects of relics
 * outside of the main App. By moving these helpers here, you can easily
 * tweak the behaviour of relics (e.g. how much sanity a torch restores or
 * how combat bonuses stack) without having to touch the core game loop in
 * App.js. All functions are pure helpers – they do not hold any state and
 * instead rely on a context of callbacks passed in from App.js. When adding
 * new relic behaviours, define the raw data in game/data/relics.js and, if
 * necessary, update or add a helper here to handle new hooks.
 */

function getScaledHookAmount(percent, maxValue) {
  const pct = Number(percent) || 0;
  const max = Number(maxValue) || 0;
  if (!pct || max <= 0) return 0;
  const scaled = Math.round((max * pct) / 100);
  if (pct > 0) return Math.max(1, scaled);
  if (pct < 0) return Math.min(-1, scaled);
  return 0;
}

/**
 * Aggregate combat modifiers from a list of relics. The structure returned
 * mirrors the shape expected by the combat engine. Values are clamped
 * where appropriate to avoid negative or excessively high numbers.
 *
 * @param {Array} relics List of relic objects (each may have a `combat` field)
 * @returns {Object} mods object containing aggregated combat modifiers
 */
export function computeCombatMods(relics) {
  // Build an aggregated mods object. Start with explicit numeric fields set to 0 so they always exist.
  const mods = {
    startArmor: 0,
    startHp: 0,
    startSp: 0,
    startNextCardCost0: 0,
    startReflectPct: 0,
    startNegateNextDamage: 0,
    turnStartAttackBonus: 0,
    turnStartArmor: 0,
    firstTurnExtraDraw: 0,
    attackBonusFlat: 0,
    invertedHeart: false,
  };
  // Merge combat fields from every owned relic. Unknown numeric fields are summed, booleans ORed.
  for (const r of relics || []) {
    const c = r?.combat || {};
    for (const key of Object.keys(c)) {
      const value = c[key];
      const targetKey = key;
      if (typeof value === 'number') {
        mods[targetKey] = (mods[targetKey] || 0) + Number(value);
      } else if (typeof value === 'boolean') {
        mods[targetKey] = Boolean(mods[targetKey] || value);
      } else {
        // For any other type (e.g. strings), copy it if not already set
        if (mods[targetKey] === undefined) mods[targetKey] = value;
      }
    }
  }
  // Clamp / normalize aggregated values for known numeric fields
  mods.startArmor = Math.max(0, Math.floor(Number(mods.startArmor) || 0));
  mods.startHp = Math.floor(Number(mods.startHp) || 0);
  mods.startSp = Math.floor(Number(mods.startSp) || 0);
  mods.startNextCardCost0 = Math.max(0, Math.floor(Number(mods.startNextCardCost0) || 0));
  mods.startReflectPct = Math.max(0, Math.min(100, Math.floor(Number(mods.startReflectPct) || 0)));
  mods.startNegateNextDamage = Math.max(0, Math.floor(Number(mods.startNegateNextDamage) || 0));
  mods.turnStartAttackBonus = Math.max(0, Math.floor(Number(mods.turnStartAttackBonus) || 0));
  mods.turnStartArmor = Math.max(0, Math.floor(Number(mods.turnStartArmor) || 0));
  mods.firstTurnExtraDraw = Math.max(0, Math.floor(Number(mods.firstTurnExtraDraw) || 0));
  mods.attackBonusFlat = Math.max(0, Math.floor(Number(mods.attackBonusFlat) || 0));
  return mods;
}

/**
 * Apply chest opening hooks for all owned relics. Each relic may define an
 * `onOpenChest` object under its `dungeon` field with keys such as
 * `sanity`, `keys`, `teeth`, `potions` or `nextCombatCost0`. For each
 * property present, the corresponding callback on the context is invoked.
 *
 * @param {Array} relics List of relics to iterate over
 * @param {Object} ctx Context containing functions to mutate game state.
 *                     Supported callbacks: addSanity, addKeys, addTeeth,
 *                     addPotions, addNextCombatCost0.
 */
export function applyChestHooks(relics, ctx = {}) {
  for (const r of relics || []) {
    const hook = r?.dungeon?.onOpenChest;
    if (!hook) continue;
    const {
      sanity,
      keys,
      teeth,
      potions,
      nextCombatCost0,
    } = hook;
    const hpBonus = Number(hook.hp ?? hook.HP ?? 0) || 0;
    if (sanity != null && ctx.addSanity) {
      const n = Number(sanity) || 0;
      if (n) ctx.addSanity(n);
    }
    if (hpBonus && ctx.addHp) {
      ctx.addHp(hpBonus);
    }
    if (keys != null && ctx.addKeys) {
      const n = Number(keys) || 0;
      if (n) ctx.addKeys(n);
    }
    if (teeth != null && ctx.addTeeth) {
      const n = Number(teeth) || 0;
      if (n) ctx.addTeeth(n);
    }
    if (potions != null && ctx.addPotions) {
      const n = Number(potions) || 0;
      if (n) ctx.addPotions(n);
    }
    if (nextCombatCost0 != null && ctx.addNextCombatCost0) {
      const n = Number(nextCombatCost0) || 0;
      if (n) ctx.addNextCombatCost0(n);
    }
  }
}

/**
 * Apply torch lighting hooks for all owned relics. Hooks live under
 * `dungeon.onLightTorch` on each relic. Supported properties include
 * `nextCombatCost0`, `nextCombatNegateNextDamage`, `sanity`, `potions` and
 * `keys`. Matching callbacks should be provided on the context.
 *
 * @param {Array} relics List of relic objects
 * @param {Object} ctx Context containing functions: addNextCombatCost0,
 *                     addNextCombatNegateNextDamage, addSanity,
 *                     addPotions, addKeys
 */
export function applyTorchHooks(relics, ctx = {}) {
  for (const r of relics || []) {
    const hook = r?.dungeon?.onLightTorch;
    if (!hook) continue;
    const {
      nextCombatCost0,
      nextCombatNegateNextDamage,
      nextCombatStartArmor,
      sanity,
      potions,
      keys,
    } = hook;
    if (nextCombatCost0 != null && ctx.addNextCombatCost0) {
      const n = Number(nextCombatCost0) || 0;
      if (n) ctx.addNextCombatCost0(n);
    }
    if (nextCombatNegateNextDamage != null && ctx.addNextCombatNegateNextDamage) {
      const n = Number(nextCombatNegateNextDamage) || 0;
      if (n) ctx.addNextCombatNegateNextDamage(n);
    }
    if (nextCombatStartArmor != null && ctx.addNextCombatStartArmor) {
      const n = Number(nextCombatStartArmor) || 0;
      if (n) ctx.addNextCombatStartArmor(n);
    }
    if (sanity != null && ctx.addSanity) {
      const n = Number(sanity) || 0;
      if (n) ctx.addSanity(n);
    }
    if (potions != null && ctx.addPotions) {
      const n = Number(potions) || 0;
      if (n) ctx.addPotions(n);
    }
    if (keys != null && ctx.addKeys) {
      const n = Number(keys) || 0;
      if (n) ctx.addKeys(n);
    }
  }
}

/**
 * Apply rest hooks for all owned relics. Rest hooks live under
 * `dungeon.onRest` on each relic and may contain fields: `sanity`,
 * `sanityPct`, `hp`, `hpPct` (or `HP` for legacy), `keys`, `teeth`,
 * `potions`, `nextCombatCost0`.
 * Provide matching callbacks on the context to mutate game state.
 *
 * @param {Array} relics List of relic objects
 * @param {Object} ctx Context containing functions: addSanity, addHp,
 *                     addKeys, addTeeth, addPotions, addNextCombatCost0,
 *                     and optional maxHp/maxSp values for percentage hooks
 */
export function applyRestHooks(relics, ctx = {}) {
  for (const r of relics || []) {
    const hook = r?.dungeon?.onRest;
    if (!hook) continue;
    const {
      sanity,
      sanityPct,
      keys,
      teeth,
      potions,
      nextCombatCost0,
    } = hook;
    // Handle HP aliases (hp / HP)
    const hpBonus = Number(hook.hp ?? hook.HP ?? 0) || 0;
    const hpPctBonus = Number(hook.hpPct ?? 0) || 0;
    if (sanity != null && ctx.addSanity) {
      const n = Number(sanity) || 0;
      if (n) ctx.addSanity(n);
    }
    if (sanityPct && ctx.addSanity) {
      const n = getScaledHookAmount(sanityPct, ctx.maxSp ?? ctx.maxSanity);
      if (n) ctx.addSanity(n);
    }
    if (hpBonus && ctx.addHp) {
      ctx.addHp(hpBonus);
    }
    if (hpPctBonus && ctx.addHp) {
      const n = getScaledHookAmount(hpPctBonus, ctx.maxHp);
      if (n) ctx.addHp(n);
    }
    if (keys != null && ctx.addKeys) {
      const n = Number(keys) || 0;
      if (n) ctx.addKeys(n);
    }
    if (teeth != null && ctx.addTeeth) {
      const n = Number(teeth) || 0;
      if (n) ctx.addTeeth(n);
    }
    if (potions != null && ctx.addPotions) {
      const n = Number(potions) || 0;
      if (n) ctx.addPotions(n);
    }
    if (nextCombatCost0 != null && ctx.addNextCombatCost0) {
      const n = Number(nextCombatCost0) || 0;
      if (n) ctx.addNextCombatCost0(n);
    }
  }
}

/**
 * Compute per-floor charges for relic abilities such as bypassing enemies or
 * opening chests for free. Returns the base number of charges for each
 * mechanic based on the owned relics.
 *
 * @param {Array} relics List of relic objects
 * @returns {Object} { bypassEnemyCharges, freeChestCharges }
 */
export function getPerFloorCharges(relics) {
  let bypassEnemyCharges = 0;
  let freeChestCharges = 0;
  for (const r of relics || []) {
    const d = r?.dungeon || {};
    bypassEnemyCharges += Number(d.bypassEnemyPerFloor) || 0;
    freeChestCharges += Number(d.freeChestPerFloor) || 0;
  }
  return { bypassEnemyCharges, freeChestCharges };
}

/**
 * Compute the trap activation chance multiplier by multiplying all relic
 * multipliers together. If a relic does not define a multiplier, it is
 * treated as 1 (no change).
 *
 * @param {Array} relics List of relic objects
 * @returns {Number} Final trap activation chance multiplier
 */
export function getTrapActivationChanceMultiplier(relics) {
  return (relics || []).reduce((mul, r) => {
    const m = Number(r?.dungeon?.trapActivationChanceMultiplier);
    return mul * (Number.isFinite(m) ? m : 1);
  }, 1);
}

/**
 * Compute the per-tile HP regeneration bonus from relics. Returns the sum of
 * all `perTileHpRegen` values defined under the `dungeon` field of relics.
 *
 * @param {Array} relics List of relic objects
 * @returns {Number} Total HP regeneration per tile
 */
export function getPerTileHpRegen(relics) {
  return (relics || []).reduce((sum, r) => {
    const n = Number(r?.dungeon?.perTileHpRegen);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

/**
 * Compute the per-tile SP regeneration bonus from relics. Similar to
 * `getPerTileHpRegen` but for SP.
 *
 * @param {Array} relics List of relic objects
 * @returns {Number} Total SP regeneration per tile
 */
export function getPerTileSpRegen(relics) {
  return (relics || []).reduce((sum, r) => {
    const n = Number(r?.dungeon?.perTileSpRegen);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}
